import { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  Check,
  CheckCheck,
  CheckCircle2,
  XCircle,
  Download,
  Pencil,
  Plus,
  RefreshCw,
  Trash2,
  Upload,
  CalendarDays,
  CalendarClock,
  ListChecks,
  FileSpreadsheet,
  ShieldCheck,
  PenSquare,
  Type as TypeIcon,
  FileText,
  Sparkles,
  Flag,
  ArrowRight,
} from "lucide-react";
import { AuthContext } from "../../../Moduels/Context/AuthContext";
import { canManageHolidays } from "../../../rbac/access";
import {
  createWfHoliday,
  deleteWfHoliday,
  downloadHolidayTemplate,
  fetchWfHolidays,
  importHolidayUpload,
  previewHolidayUpload,
  updateWfHoliday,
} from "../../../Moduels/attendance/wfApi";
import DatePickerField from "../../common/DatePickerField";
import { parseDateValue, toDateValue } from "../../common/datePickerUtils";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

// Fixed-date Indian national / restricted holidays (same date every year).
// Variable / lunar festivals (Holi, Diwali, Eid, etc.) must be added manually
// because their Gregorian date shifts year to year.
const INDIAN_FIXED_HOLIDAYS = [
  { m: 1, d: 1, name: "New Year's Day", optional: true },
  { m: 1, d: 14, name: "Makar Sankranti / Pongal", optional: true },
  { m: 1, d: 26, name: "Republic Day", optional: false },
  { m: 4, d: 14, name: "Ambedkar Jayanti", optional: false },
  { m: 5, d: 1, name: "Labour Day", optional: false },
  { m: 8, d: 15, name: "Independence Day", optional: false },
  { m: 10, d: 2, name: "Gandhi Jayanti", optional: false },
  { m: 12, d: 25, name: "Christmas Day", optional: false },
];

const pad2 = (n) => String(n).padStart(2, "0");

/** Max consecutive days for manual date-range entry (frontend guard only). */
const MAX_HOLIDAY_RANGE_DAYS = 90;

function enumerateDateRange(fromIso, toIso) {
  const start = parseDateValue(fromIso);
  const end = parseDateValue(toIso);
  if (!start || !end || end < start) return [];
  const dates = [];
  const cur = new Date(start);
  while (cur <= end) {
    dates.push(toDateValue(cur));
    cur.setDate(cur.getDate() + 1);
  }
  return dates;
}

const MONTH_TONE = [
  "from-sky-500 to-blue-600",
  "from-pink-500 to-rose-600",
  "from-emerald-500 to-teal-600",
  "from-amber-500 to-orange-600",
  "from-indigo-500 to-purple-600",
  "from-cyan-500 to-sky-600",
  "from-fuchsia-500 to-pink-600",
  "from-lime-500 to-green-600",
  "from-violet-500 to-purple-600",
  "from-orange-500 to-red-600",
  "from-teal-500 to-cyan-600",
  "from-rose-500 to-fuchsia-600",
];

function formatHolDate(iso) {
  if (!iso) return "—";
  const d = new Date(`${iso}T00:00:00`);
  return d.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
}

function SectionCard({ accent, icon: Icon, iconBg, title, titleColor, subtitle, children, className = "" }) {
  return (
    <div className={`relative bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition overflow-hidden h-full flex flex-col ${className}`}>
      <div className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${accent}`} />
      <div className="p-5 flex-1 flex flex-col">
        <div className="flex items-start gap-3 mb-4">
          <div className={`shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br ${iconBg} text-white flex items-center justify-center shadow-sm`}>
            <Icon size={18} />
          </div>
          <div className="min-w-0">
            <h2 className={`font-bold text-base ${titleColor}`}>{title}</h2>
            {subtitle ? <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p> : null}
          </div>
        </div>
        <div className="flex-1 flex flex-col">{children}</div>
      </div>
    </div>
  );
}

function KpiTile({ icon: Icon, label, value, sub, bg, iconBg, border, accent }) {
  return (
    <div className={`relative p-4 rounded-2xl bg-gradient-to-br ${bg} border ${border} overflow-hidden`}>
      <div className={`absolute -top-6 -right-6 w-20 h-20 rounded-full bg-gradient-to-br ${accent} opacity-25`} />
      <div className="relative flex items-start justify-between">
        <div>
          <p className="text-[10px] uppercase tracking-wider font-bold text-slate-600">{label}</p>
          <p className="text-3xl font-extrabold text-slate-900 mt-1">{value}</p>
          {sub ? <p className="text-[11px] text-slate-500 mt-1">{sub}</p> : null}
        </div>
        <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${iconBg} text-white flex items-center justify-center shadow-md`}>
          <Icon size={18} />
        </div>
      </div>
    </div>
  );
}

export default function HolidayCalendarMvp() {
  const { role } = useContext(AuthContext);
  const canManage = canManageHolidays(role);
  const year = new Date().getFullYear();
  const [viewYear, setViewYear] = useState(year);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [uploadFile, setUploadFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [showAllHolidays, setShowAllHolidays] = useState(true);
  const [applyingIndian, setApplyingIndian] = useState(false);
  const [editId, setEditId] = useState(null);
  const [holidayDuration, setHolidayDuration] = useState("single");
  const [addingHolidays, setAddingHolidays] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkEditIds, setBulkEditIds] = useState([]);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [form, setForm] = useState({
    holiday_date: "",
    from_date: "",
    to_date: "",
    name: "",
    holiday_type: "National",
    description: "",
  });
  const manualEntryRef = useRef(null);
  const nameInputRef = useRef(null);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const rows = await fetchWfHolidays({
        from_date: `${viewYear}-01-01`,
        to_date: `${viewYear}-12-31`,
      });
      setHolidays(Array.isArray(rows) ? rows : []);
    } catch (e) {
      setHolidays([]);
      setError(e.message || "Failed to load holidays");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewYear]);

  useEffect(() => {
    const validIds = new Set(holidays.map((h) => h.holiday_id).filter(Boolean));
    setSelectedIds((prev) => {
      const next = new Set([...prev].filter((id) => validIds.has(id)));
      return next.size === prev.size ? prev : next;
    });
    setBulkEditIds((prev) => {
      const next = prev.filter((id) => validIds.has(id));
      return next.length === prev.length ? prev : next;
    });
  }, [holidays]);

  const monthHolidays = useMemo(() => {
    return holidays
      .filter((h) => {
        const m = Number(String(h.holiday_date).slice(5, 7));
        return m === selectedMonth;
      })
      .sort((a, b) => String(a.holiday_date).localeCompare(String(b.holiday_date)));
  }, [holidays, selectedMonth]);

  const displayHolidays = useMemo(() => {
    if (showAllHolidays) {
      return [...holidays].sort((a, b) => String(a.holiday_date).localeCompare(String(b.holiday_date)));
    }
    return monthHolidays;
  }, [holidays, monthHolidays, showAllHolidays]);

  const todayIso = new Date().toISOString().slice(0, 10);

  const upcomingHolidays = useMemo(() => {
    return holidays
      .filter((h) => String(h.holiday_date).slice(0, 10) >= todayIso)
      .sort((a, b) => String(a.holiday_date).localeCompare(String(b.holiday_date)));
  }, [holidays, todayIso]);

  const completedHolidays = useMemo(() => {
    return holidays
      .filter((h) => String(h.holiday_date).slice(0, 10) < todayIso)
      .sort((a, b) => String(b.holiday_date).localeCompare(String(a.holiday_date)));
  }, [holidays, todayIso]);

  const daysUntil = (iso) => {
    if (!iso) return null;
    const target = new Date(`${iso}T00:00:00`);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return Math.round((target - today) / (1000 * 60 * 60 * 24));
  };

  const dayOfWeek = (iso) => {
    if (!iso) return "";
    const d = new Date(`${iso}T00:00:00`);
    return d.toLocaleDateString(undefined, { weekday: "short" });
  };

  const monthCounts = useMemo(() => {
    const counts = Array(12).fill(0);
    for (const h of holidays) {
      const m = Number(String(h.holiday_date).slice(5, 7));
      if (m >= 1 && m <= 12) counts[m - 1] += 1;
    }
    return counts;
  }, [holidays]);

  /** Hide Apply Indian Holidays once all fixed-date holidays for the year exist. */
  const indianHolidayStatus = useMemo(() => {
    const existingDates = new Set(
      holidays.map((h) => String(h.holiday_date).slice(0, 10))
    );
    const targets = INDIAN_FIXED_HOLIDAYS.map((h) => ({
      iso: `${viewYear}-${pad2(h.m)}-${pad2(h.d)}`,
      name: h.name,
    }));
    const missing = targets.filter((t) => !existingDates.has(t.iso));
    const presentCount = targets.length - missing.length;
    return {
      total: targets.length,
      presentCount,
      missingCount: missing.length,
      allApplied: missing.length === 0,
      missingNames: missing.map((m) => m.name),
    };
  }, [holidays, viewYear]);

  const showApplyIndianCta = canManage && !loading && !indianHolidayStatus.allApplied;

  const rangeDayCount = useMemo(() => {
    if (holidayDuration !== "range" || !form.from_date || !form.to_date) return null;
    const count = enumerateDateRange(form.from_date, form.to_date).length;
    return count > 0 ? count : null;
  }, [holidayDuration, form.from_date, form.to_date]);

  const resetForm = () => {
    setEditId(null);
    setBulkEditIds([]);
    setHolidayDuration("single");
    setForm({
      holiday_date: "",
      from_date: "",
      to_date: "",
      name: "",
      holiday_type: "National",
      description: "",
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const toggleHolidaySelection = (holidayId) => {
    if (!holidayId) return;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(holidayId)) next.delete(holidayId);
      else next.add(holidayId);
      return next;
    });
  };

  const toggleSelectAllDisplayed = (checked) => {
    if (checked) {
      setSelectedIds(new Set(displayHolidays.map((h) => h.holiday_id).filter(Boolean)));
    } else {
      clearSelection();
    }
  };

  const allDisplayedSelected =
    displayHolidays.length > 0 &&
    displayHolidays.every((h) => h.holiday_id && selectedIds.has(h.holiday_id));

  const startBulkEdit = () => {
    const ids = [...selectedIds];
    if (!ids.length) return;
    const rows = holidays.filter((h) => selectedIds.has(h.holiday_id));
    const names = rows.map((r) => r.name || "");
    const allSameName = names.length > 0 && names.every((n) => n === names[0]);
    const types = rows.map((r) => (r.is_optional ? "Optional" : "National"));
    const allSameType = types.length > 0 && types.every((t) => t === types[0]);

    setEditId(null);
    setBulkEditIds(ids);
    setHolidayDuration("single");
    setForm({
      holiday_date: "",
      from_date: "",
      to_date: "",
      name: allSameName ? names[0] : rows[0]?.name || "",
      holiday_type: allSameType ? types[0] : "National",
      description: "",
    });
    setInfo(`Bulk editing ${ids.length} holiday(s) — update common fields below. Individual dates will not change.`);
    setError("");
    requestAnimationFrame(() => {
      manualEntryRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      setTimeout(() => nameInputRef.current?.focus(), 280);
    });
  };

  const startEdit = (row) => {
    const id = row?.holiday_id || row?.id;
    if (!id) return;
    setEditId(id);
    setBulkEditIds([]);
    setHolidayDuration("single");
    setForm({
      holiday_date: String(row.holiday_date || "").slice(0, 10),
      from_date: "",
      to_date: "",
      name: row.name || "",
      holiday_type: row.is_optional ? "Optional" : "National",
      description: row.description || "",
    });
    setInfo(`Editing: ${row.name || "holiday"} — update the fields and click Save Changes.`);
    setError("");
    // Bring Manual Entry into view so the holiday is ready to edit
    requestAnimationFrame(() => {
      manualEntryRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      setTimeout(() => nameInputRef.current?.focus(), 280);
    });
  };

  const onAdd = async (e) => {
    e.preventDefault();
    if (!canManage || addingHolidays) return;

    const payload = {
      name: form.name || null,
      is_optional: form.holiday_type === "Optional",
    };

    if (holidayDuration === "range") {
      if (!form.from_date) {
        setError("From date is required.");
        return;
      }
      if (!form.to_date) {
        setError("To date is required.");
        return;
      }
      const from = parseDateValue(form.from_date);
      const to = parseDateValue(form.to_date);
      if (!from || !to) {
        setError("Enter valid From and To dates.");
        return;
      }
      if (to < from) {
        setError("To date cannot be before From date.");
        return;
      }
      const dates = enumerateDateRange(form.from_date, form.to_date);
      if (dates.length === 0) {
        setError("Enter a valid date range.");
        return;
      }
      if (dates.length > MAX_HOLIDAY_RANGE_DAYS) {
        setError(`Date range cannot exceed ${MAX_HOLIDAY_RANGE_DAYS} days. Use Holiday Upload for larger batches.`);
        return;
      }

      setAddingHolidays(true);
      setError("");
      setInfo("");
      let created = 0;
      let skipped = 0;
      const failures = [];
      for (const iso of dates) {
        try {
          await createWfHoliday({
            holiday_date: iso,
            ...payload,
          });
          created += 1;
        } catch (err) {
          const msg = String(err?.message || "");
          if (msg.toLowerCase().includes("already exists") || msg.toLowerCase().includes("duplicate")) {
            skipped += 1;
          } else {
            failures.push(`${formatHolDate(iso)}: ${msg || "failed"}`);
          }
        }
      }
      await load();
      setAddingHolidays(false);
      const parts = [
        `${created} added`,
        skipped ? `${skipped} skipped` : null,
        failures.length ? `${failures.length} failed` : null,
      ].filter(Boolean);
      if (failures.length) {
        setError(`Some holidays failed: ${failures.slice(0, 3).join("; ")}${failures.length > 3 ? "…" : ""}`);
      }
      if (created > 0 || skipped > 0) {
        setInfo(`Holidays: ${parts.join(", ")}.`);
      } else if (!failures.length) {
        setInfo("No holidays were added.");
      }
      if (created > 0) resetForm();
      return;
    }

    if (!form.holiday_date) {
      setError("Holiday date is required.");
      return;
    }
    setError("");
    try {
      await createWfHoliday({
        holiday_date: form.holiday_date,
        ...payload,
      });
      setInfo("Holiday added.");
      resetForm();
      await load();
    } catch (err) {
      setError(err.message || "Add failed");
    }
  };

  const onEdit = async () => {
    if (!canManage || !editId) {
      setError("Select a holiday first using Edit in the Holiday List.");
      return;
    }
    if (!form.name?.trim()) {
      setError("Holiday name is required.");
      return;
    }
    const original = holidays.find((h) => h.holiday_id === editId);
    const newDate = form.holiday_date?.slice(0, 10);
    const oldDate = original ? String(original.holiday_date).slice(0, 10) : null;

    setError("");
    try {
      if (newDate && oldDate && newDate !== oldDate) {
        if (!window.confirm(`Change date from ${formatHolDate(oldDate)} to ${formatHolDate(newDate)}? This replaces the holiday entry.`)) {
          return;
        }
        await deleteWfHoliday(editId);
        await createWfHoliday({
          holiday_date: newDate,
          name: form.name || null,
          is_optional: form.holiday_type === "Optional",
        });
        setInfo("Holiday updated (date changed — entry replaced).");
      } else {
        await updateWfHoliday(editId, {
          name: form.name || null,
          is_optional: form.holiday_type === "Optional",
        });
        setInfo("Holiday updated.");
      }
      resetForm();
      await load();
    } catch (err) {
      setError(err.message || "Update failed");
    }
  };

  const onBulkUpdate = async () => {
    if (!canManage || bulkEditIds.length === 0 || bulkBusy) return;
    if (!form.name?.trim()) {
      setError("Holiday name is required.");
      return;
    }
    setBulkBusy(true);
    setError("");
    setInfo("");
    const payload = {
      name: form.name || null,
      is_optional: form.holiday_type === "Optional",
    };
    let updated = 0;
    const failures = [];
    for (const id of bulkEditIds) {
      try {
        await updateWfHoliday(id, payload);
        updated += 1;
      } catch (err) {
        const row = holidays.find((h) => h.holiday_id === id);
        failures.push(`${formatHolDate(row?.holiday_date)}: ${err.message || "failed"}`);
      }
    }
    await load();
    setBulkBusy(false);
    clearSelection();
    resetForm();
    if (failures.length) {
      setError(`Some holidays failed: ${failures.slice(0, 3).join("; ")}${failures.length > 3 ? "…" : ""}`);
    }
    if (updated > 0) {
      setInfo(`${updated} holiday${updated !== 1 ? "s" : ""} updated.`);
    } else if (!failures.length) {
      setInfo("No holidays were updated.");
    }
  };

  const onBulkDelete = async () => {
    if (!canManage || selectedIds.size === 0 || bulkBusy) return;
    const count = selectedIds.size;
    if (
      !window.confirm(
        `Delete Selected Holidays?\n\nYou are about to delete ${count} holiday${count !== 1 ? "s" : ""}.`
      )
    ) {
      return;
    }
    setBulkBusy(true);
    setError("");
    setInfo("");
    let deleted = 0;
    const failures = [];
    const ids = [...selectedIds];
    for (const id of ids) {
      try {
        await deleteWfHoliday(id);
        deleted += 1;
        if (editId === id) resetForm();
      } catch (err) {
        const row = holidays.find((h) => h.holiday_id === id);
        failures.push(`${formatHolDate(row?.holiday_date)}: ${err.message || "failed"}`);
      }
    }
    await load();
    setBulkBusy(false);
    clearSelection();
    if (failures.length) {
      setError(`Some holidays failed: ${failures.slice(0, 3).join("; ")}${failures.length > 3 ? "…" : ""}`);
    }
    if (deleted > 0) {
      setInfo(`${deleted} holiday${deleted !== 1 ? "s" : ""} deleted.`);
    }
  };

  const onDeleteSelected = async () => {
    if (!editId) {
      setError("Select a holiday first using Edit in the Holiday List.");
      return;
    }
    const row = holidays.find((h) => h.holiday_id === editId);
    if (!row) return;
    await onDelete(row);
    resetForm();
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (bulkEditIds.length > 0) {
      await onBulkUpdate();
    } else if (editId) {
      await onEdit();
    } else {
      await onAdd(e);
    }
  };

  const onDelete = async (row) => {
    if (!canManage || !window.confirm(`Delete ${row.name || "holiday"}?`)) return;
    try {
      await deleteWfHoliday(row.holiday_id);
      setInfo("Holiday deleted.");
      if (editId === row.holiday_id) resetForm();
      await load();
    } catch (err) {
      setError(err.message || "Delete failed");
    }
  };

  const applyIndianHolidays = async () => {
    if (!canManage) return;
    const targets = INDIAN_FIXED_HOLIDAYS.map((h) => ({
      iso: `${viewYear}-${pad2(h.m)}-${pad2(h.d)}`,
      name: h.name,
      optional: h.optional,
    }));
    const existingDates = new Set(
      holidays.map((h) => String(h.holiday_date).slice(0, 10))
    );
    const toCreate = targets.filter((t) => !existingDates.has(t.iso));
    if (toCreate.length === 0) {
      setInfo(`All ${targets.length} fixed Indian holidays for ${viewYear} are already present. The Apply button is hidden.`);
      setError("");
      return;
    }
    if (
      !window.confirm(
        `Add ${toCreate.length} Indian national holiday(s) for ${viewYear}? Existing entries will be kept untouched.`
      )
    ) {
      return;
    }
    setApplyingIndian(true);
    setError("");
    setInfo("");
    let created = 0;
    let skipped = 0;
    const failures = [];
    for (const t of toCreate) {
      try {
        await createWfHoliday({
          holiday_date: t.iso,
          name: t.name,
          is_optional: t.optional,
        });
        created += 1;
      } catch (err) {
        const msg = String(err?.message || "");
        if (msg.toLowerCase().includes("already exists") || msg.toLowerCase().includes("duplicate")) {
          skipped += 1;
        } else {
          failures.push(`${t.name}: ${msg || "failed"}`);
        }
      }
    }
    await load();
    setApplyingIndian(false);
    const alreadyThere = targets.length - toCreate.length + skipped;
    const parts = [
      `${created} added`,
      alreadyThere ? `${alreadyThere} already present` : null,
      failures.length ? `${failures.length} failed` : null,
    ].filter(Boolean);
    if (failures.length) {
      setError(`Some holidays failed: ${failures.slice(0, 3).join("; ")}${failures.length > 3 ? "…" : ""}`);
    }
    setInfo(`Indian holidays for ${viewYear}: ${parts.join(", ")}.`);
  };

  const onDownloadTemplate = async () => {
    try {
      const blob = await downloadHolidayTemplate();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "holiday_calendar_template.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.message || "Template download failed");
    }
  };

  const onPreviewUpload = async () => {
    if (!uploadFile) return;
    setUploadBusy(true);
    setError("");
    try {
      const res = await previewHolidayUpload(uploadFile);
      setPreview(res);
      if (res.invalid_rows > 0 && res.duplicate_rows > 0) {
        setInfo(
          `${res.valid_rows} valid, ${res.invalid_rows} invalid (${res.duplicate_rows} duplicate date(s) — already in calendar or repeated in file).`
        );
      } else if (res.invalid_rows > 0) {
        setInfo(`${res.valid_rows} valid, ${res.invalid_rows} invalid row(s). See details below.`);
      } else {
        setInfo(`All ${res.valid_rows} row(s) are valid and ready to import.`);
      }
    } catch (e) {
      setPreview(null);
      setError(e.message || "Preview failed");
    } finally {
      setUploadBusy(false);
    }
  };

  const onImportUpload = async () => {
    if (!uploadFile) return;
    setUploadBusy(true);
    setError("");
    try {
      const res = await importHolidayUpload(uploadFile);
      const skipped = res.skipped || 0;
      setInfo(
        skipped
          ? `Imported ${res.created} holiday(s). ${skipped} row(s) skipped (duplicates or errors).`
          : `Imported ${res.created} holiday(s).`
      );
      setPreview(null);
      setUploadFile(null);
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : detail?.message || e.message || "Import failed");
      if (detail?.preview) setPreview(detail.preview);
    } finally {
      setUploadBusy(false);
    }
  };

  return (
    <div className="space-y-5">
      {/* MESSAGES */}
      {error ? (
        <div className="flex items-start gap-2 bg-gradient-to-r from-rose-50 to-red-50 border-l-4 border-rose-500 text-rose-800 rounded-r-xl px-4 py-3 text-sm shadow-sm">
          <XCircle size={16} className="shrink-0 mt-0.5 text-rose-600" />
          <span className="font-semibold">{error}</span>
        </div>
      ) : null}
      {info ? (
        <div className="flex items-start gap-2 bg-gradient-to-r from-emerald-50 to-teal-50 border-l-4 border-emerald-500 text-emerald-800 rounded-r-xl px-4 py-3 text-sm shadow-sm">
          <CheckCircle2 size={16} className="shrink-0 mt-0.5 text-emerald-600" />
          <span className="font-semibold">{info}</span>
        </div>
      ) : null}

      {/* 1. HOLIDAY DASHBOARD — full width horizontal */}
      <SectionCard
        accent="from-emerald-500 to-teal-600"
        icon={ListChecks}
        iconBg="from-emerald-500 to-teal-600"
        title="Holiday Calendar Dashboard"
        titleColor="text-emerald-900"
        subtitle={`Snapshot of ${viewYear} holidays.`}
      >
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <KpiTile
            icon={CalendarDays}
            label="Total Holidays This Year"
            value={loading ? "—" : holidays.length}
            bg="from-emerald-50 to-teal-50"
            iconBg="from-emerald-500 to-teal-600"
            border="border-emerald-200"
            accent="from-emerald-400 to-teal-500"
          />
          <KpiTile
            icon={CalendarClock}
            label="Upcoming Holidays"
            value={loading ? "—" : upcomingHolidays.length}
            sub={upcomingHolidays[0] ? `Next: ${formatHolDate(upcomingHolidays[0].holiday_date)}` : "None scheduled"}
            bg="from-amber-50 to-orange-50"
            iconBg="from-amber-500 to-orange-600"
            border="border-amber-200"
            accent="from-amber-400 to-orange-500"
          />
          <KpiTile
            icon={CheckCheck}
            label="Completed Holidays"
            value={loading ? "—" : completedHolidays.length}
            sub={completedHolidays[0] ? `Last: ${formatHolDate(completedHolidays[0].holiday_date)}` : "None yet"}
            bg="from-violet-50 to-fuchsia-50"
            iconBg="from-violet-500 to-fuchsia-600"
            border="border-violet-200"
            accent="from-violet-400 to-fuchsia-500"
          />
        </div>

        {showApplyIndianCta ? (
          <div className="mt-4 relative overflow-hidden rounded-2xl border-2 border-amber-300/80 bg-gradient-to-r from-amber-50 via-orange-50 to-amber-50 p-4 sm:p-5 shadow-[0_0_0_4px_rgba(251,191,36,0.18)] animate-[hcIndianPulse_2.2s_ease-in-out_infinite]">
            <style>{`
              @keyframes hcIndianPulse {
                0%, 100% { box-shadow: 0 0 0 4px rgba(251,191,36,0.18), 0 8px 24px rgba(245,158,11,0.18); }
                50% { box-shadow: 0 0 0 8px rgba(251,191,36,0.28), 0 12px 32px rgba(245,158,11,0.28); }
              }
              @keyframes hcIndianShine {
                0% { transform: translateX(-120%); }
                100% { transform: translateX(120%); }
              }
            `}</style>
            <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden>
              <div className="absolute inset-y-0 w-1/3 bg-gradient-to-r from-transparent via-white/50 to-transparent animate-[hcIndianShine_2.8s_linear_infinite]" />
            </div>
            <div className="relative flex flex-col lg:flex-row lg:items-center gap-4">
              <div className="flex items-start gap-3 flex-1 min-w-0">
                <span className="shrink-0 w-12 h-12 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-600 text-white flex items-center justify-center shadow-lg ring-4 ring-amber-200/70">
                  <Flag size={22} className="animate-pulse" />
                </span>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-sm sm:text-base font-extrabold text-amber-950">
                      One-click setup — Apply Indian Holidays ({viewYear})
                    </p>
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide bg-amber-500 text-white shadow-sm">
                      <Sparkles size={11} />
                      Recommended
                    </span>
                  </div>
                  <p className="text-[12px] text-amber-950/75 mt-1 leading-relaxed">
                    Adds {indianHolidayStatus.total} fixed-date national &amp; restricted holidays
                    ({indianHolidayStatus.missingCount} still missing). Lunar festivals (Holi, Diwali, Eid…)
                    must be added manually — their dates change each year.
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={applyIndianHolidays}
                disabled={applyingIndian || loading}
                className="relative shrink-0 inline-flex items-center justify-center gap-2.5 px-6 py-3.5 min-h-[52px] rounded-2xl text-sm sm:text-base font-extrabold text-white bg-gradient-to-r from-[#0f2d52] via-[#1a437d] to-[#2a5fa3] shadow-[0_10px_28px_rgba(26,67,125,0.45)] hover:shadow-[0_14px_34px_rgba(26,67,125,0.55)] hover:-translate-y-0.5 active:translate-y-0 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0 transition-all ring-4 ring-sky-300/50 hover:ring-sky-400/70"
                title="Adds Republic Day, Independence Day, Gandhi Jayanti, Christmas, Labour Day, Ambedkar Jayanti, New Year, Pongal/Makar Sankranti for the selected year."
              >
                <Flag size={18} className={applyingIndian ? "animate-spin" : "animate-bounce"} />
                {applyingIndian ? "Adding Indian holidays…" : `Apply Indian Holidays (${viewYear})`}
                {!applyingIndian ? <ArrowRight size={18} /> : null}
              </button>
            </div>
          </div>
        ) : canManage && !loading && indianHolidayStatus.allApplied ? (
          <div className="mt-4 flex items-center gap-2.5 rounded-xl border border-emerald-200 bg-emerald-50/80 px-4 py-3 text-[12.5px] text-emerald-800">
            <CheckCircle2 size={18} className="text-emerald-600 shrink-0" />
            <span>
              <b>Indian holidays applied for {viewYear}.</b> All {indianHolidayStatus.total} fixed-date
              holidays are already on the calendar. Add lunar festivals manually if needed.
            </span>
          </div>
        ) : null}
      </SectionCard>

      {/* 2. YEARLY CALENDAR — full width horizontal */}
      <SectionCard
        accent="from-indigo-500 to-purple-600"
        icon={CalendarDays}
        iconBg="from-indigo-500 to-purple-600"
        title={`Yearly Holiday Calendar — ${viewYear}`}
        titleColor="text-indigo-900"
        subtitle="Pick a month to filter the list below."
      >
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <input
            type="number"
            className="w-24 border-2 border-slate-200 rounded-xl px-3 py-2 text-sm font-semibold focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 outline-none transition"
            value={viewYear}
            onChange={(e) => setViewYear(Number(e.target.value))}
          />
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-semibold bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-md hover:shadow-lg hover:-translate-y-0.5 disabled:opacity-50 transition"
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
            Reload
          </button>
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-12 gap-2.5 px-1">
          {MONTHS.map((m, i) => {
            const active = selectedMonth === i + 1;
            const count = monthCounts[i];
            return (
              <button
                key={m}
                type="button"
                className={`group relative px-2 py-2.5 rounded-xl text-xs font-bold transition-all duration-200 ${
                  active
                    ? `bg-gradient-to-br ${MONTH_TONE[i]} text-white shadow-md ring-4 ring-indigo-200/60 -translate-y-0.5`
                    : "bg-slate-50 text-slate-700 border border-slate-200 hover:bg-white hover:border-indigo-300 hover:-translate-y-0.5 hover:shadow"
                }`}
                onClick={() => {
                  setSelectedMonth(i + 1);
                  setShowAllHolidays(false);
                }}
              >
                <span className="relative z-0">{m.slice(0, 3)}</span>
                {count > 0 && (
                  <span
                    className={`absolute -top-1.5 -right-1.5 z-10 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[11px] leading-none font-extrabold rounded-full shadow-md ring-2 ring-white ${
                      active
                        ? "bg-white text-indigo-700"
                        : "bg-gradient-to-br from-rose-500 to-pink-600 text-white"
                    }`}
                    title={`${count} holiday${count === 1 ? "" : "s"}`}
                  >
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </SectionCard>

      {/* 3. HOLIDAY UPLOAD + MANUAL ENTRY — side by side */}
      {canManage && (
        <div className="grid lg:grid-cols-2 gap-5 items-stretch">
          <SectionCard
            accent="from-[#0f2d52] to-[#1a437d]"
            icon={Upload}
            iconBg="from-[#0f2d52] to-[#1a437d]"
            title="Holiday Upload"
            titleColor="text-[#0f2d52]"
            subtitle="Bulk import from spreadsheet."
          >
            <div className="space-y-2.5 flex-1 flex flex-col">
              <button
                type="button"
                onClick={onDownloadTemplate}
                className="w-full inline-flex items-center justify-center gap-2 px-3.5 py-2 rounded-xl text-sm font-semibold border-2 border-[#1a437d] text-[#0f2d52] hover:bg-[#0f2d52]/5 hover:border-[#0f2d52] transition"
              >
                <Download size={14} /> Download Template
              </button>

              <label className="block">
                <span className="text-[11px] uppercase tracking-wider font-bold text-slate-700 mb-1 block">Choose file</span>
                <div className="relative">
                  <input
                    type="file"
                    accept=".xlsx,.csv"
                    onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                    className="block w-full text-xs text-slate-800 file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-bold file:bg-gradient-to-r file:from-[#0f2d52] file:to-[#1a437d] file:text-white file:cursor-pointer hover:file:from-[#0a1f3a] hover:file:to-[#0f2d52] border-2 border-dashed border-slate-300 rounded-xl p-2"
                  />
                </div>
                {uploadFile && (
                  <p className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-semibold text-[#0f2d52] bg-slate-100 border border-slate-300 px-2 py-0.5 rounded-full">
                    <FileSpreadsheet size={11} /> {uploadFile.name}
                  </p>
                )}
              </label>

              <button
                type="button"
                onClick={onPreviewUpload}
                disabled={!uploadFile || uploadBusy}
                className="w-full inline-flex items-center justify-center gap-2 px-3.5 py-2 rounded-xl text-sm font-semibold border-2 border-amber-700 text-amber-900 hover:bg-amber-100 hover:border-amber-800 disabled:opacity-50 disabled:cursor-not-allowed transition"
              >
                <ShieldCheck size={14} /> Validate
              </button>
              <button
                type="button"
                onClick={onImportUpload}
                disabled={!uploadFile || uploadBusy || !preview || preview.valid_rows === 0}
                className="w-full inline-flex items-center justify-center gap-2 px-3.5 py-2 rounded-xl text-sm font-bold text-white bg-gradient-to-r from-[#0f2d52] via-[#1a437d] to-[#2a5fa3] shadow-md hover:shadow-xl hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 transition"
              >
                <Upload size={14} /> Upload Template
              </button>

              <ul className="space-y-1 pt-2 border-t border-slate-100 mt-auto">
                <li className="flex items-center gap-1.5 text-xs text-slate-600"><Check size={13} className="text-emerald-600" /> Duplicate Dates</li>
                <li className="flex items-center gap-1.5 text-xs text-slate-600"><Check size={13} className="text-emerald-600" /> Invalid Dates</li>
                <li className="flex items-center gap-1.5 text-xs text-slate-600"><Check size={13} className="text-emerald-600" /> Missing Holiday Names</li>
              </ul>

              {preview && (
                <div className="mt-2 pt-2 border-t border-slate-100">
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-[10px] font-bold border border-emerald-200">✓ {preview.valid_rows} valid</span>
                    {preview.invalid_rows > 0 && (
                      <span className="px-2 py-0.5 rounded-full bg-rose-100 text-rose-800 text-[10px] font-bold border border-rose-200">✕ {preview.invalid_rows} invalid</span>
                    )}
                    {preview.duplicate_rows > 0 && (
                      <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-bold border border-amber-200">⎘ {preview.duplicate_rows} duplicate</span>
                    )}
                  </div>
                  {preview.rows?.length > 0 && (
                    <div className="max-h-44 overflow-auto border border-slate-200 rounded-lg">
                      <table className="w-full text-[11px]">
                        <thead className="bg-slate-50 sticky top-0">
                          <tr>
                            <th className="text-left p-1.5 font-semibold text-slate-700">Row</th>
                            <th className="text-left p-1.5 font-semibold text-slate-700">Date</th>
                            <th className="text-left p-1.5 font-semibold text-slate-700">Name</th>
                            <th className="text-left p-1.5 font-semibold text-slate-700">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {preview.rows.map((r) => (
                            <tr key={r.row_index} className="border-t border-slate-100">
                              <td className="p-1.5">{r.row_index}</td>
                              <td className="p-1.5">{r.holiday_date || "—"}</td>
                              <td className="p-1.5">{r.name || "—"}</td>
                              <td className="p-1.5">
                                {r.valid ? (
                                  <span className="text-emerald-700 font-semibold">OK</span>
                                ) : (
                                  <span className="text-rose-700 font-semibold">{(r.errors || []).join("; ")}</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          </SectionCard>

          <div id="holiday-manual-entry" ref={manualEntryRef}>
          <SectionCard
            className={editId || bulkEditIds.length > 0 ? "ring-2 ring-emerald-400 shadow-lg" : ""}
            accent="from-emerald-500 to-green-600"
            icon={editId || bulkEditIds.length > 0 ? Pencil : Plus}
            iconBg="from-emerald-500 to-green-600"
            title={
              bulkEditIds.length > 0
                ? "Edit Selected Holidays"
                : editId
                  ? "Edit Holiday"
                  : "Manual Holiday Entry"
            }
            titleColor="text-emerald-900"
            subtitle={
              bulkEditIds.length > 0
                ? `Update common fields for ${bulkEditIds.length} selected holiday(s). Dates stay unchanged.`
                : editId
                  ? "Fields loaded from the list — update and save."
                  : "Add a single holiday or a continuous date range."
            }
          >
            <form onSubmit={onSubmit} className="space-y-4">
              {bulkEditIds.length > 0 ? (
                <div className="text-xs bg-gradient-to-r from-fuchsia-50 to-pink-50 border border-fuchsia-200 rounded-xl px-3 py-2 text-fuchsia-900">
                  Selected: <b>{bulkEditIds.length} holiday{bulkEditIds.length !== 1 ? "s" : ""}</b> — change name, type, or description below, then click <b>Update {bulkEditIds.length} Holidays</b>.
                </div>
              ) : editId ? (
                <div className="text-xs bg-gradient-to-r from-indigo-50 to-sky-50 border border-indigo-200 rounded-xl px-3 py-2 text-indigo-800">
                  Editing <b>{form.name || "selected holiday"}</b> — change any field below, then click <b>Save Changes</b>.
                </div>
              ) : (
                <div className="text-xs bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-slate-600">
                  Use Edit in the Holiday List Actions column to load a holiday here, or select multiple rows for bulk edit.
                </div>
              )}

              {!editId && bulkEditIds.length === 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-1.5">
                    <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-emerald-500 to-green-600 text-white flex items-center justify-center shadow-sm">
                      <CalendarClock size={12} />
                    </div>
                    <label className="text-sm font-semibold text-slate-800">Holiday Duration</label>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { value: "single", label: "Single Day" },
                      { value: "range", label: "Date Range" },
                    ].map((opt) => {
                      const active = holidayDuration === opt.value;
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => {
                            setHolidayDuration(opt.value);
                            setError("");
                          }}
                          className={`inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl border-2 text-sm font-bold transition-all duration-200 ${
                            active
                              ? "bg-gradient-to-br from-emerald-500 to-green-600 text-white border-transparent shadow-md ring-4 ring-emerald-300 -translate-y-0.5"
                              : "bg-gradient-to-br from-slate-50 to-slate-100 text-slate-700 border-slate-200 hover:-translate-y-0.5 hover:shadow"
                          }`}
                        >
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {editId || (bulkEditIds.length === 0 && holidayDuration === "single") ? (
                <div>
                  <div className="flex items-center gap-2 mb-1.5">
                    <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-sky-500 to-blue-600 text-white flex items-center justify-center shadow-sm">
                      <CalendarDays size={12} />
                    </div>
                    <label className="text-sm font-semibold text-slate-800">Holiday Date <span className="text-rose-500">*</span></label>
                  </div>
                  <DatePickerField
                    hideLabel
                    value={form.holiday_date}
                    onChange={(d) => setForm({ ...form, holiday_date: d })}
                  />
                </div>
              ) : bulkEditIds.length === 0 ? (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <div className="flex items-center gap-2 mb-1.5">
                        <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-sky-500 to-blue-600 text-white flex items-center justify-center shadow-sm">
                          <CalendarDays size={12} />
                        </div>
                        <label className="text-sm font-semibold text-slate-800">From Date <span className="text-rose-500">*</span></label>
                      </div>
                      <DatePickerField
                        hideLabel
                        value={form.from_date}
                        onChange={(d) => setForm({ ...form, from_date: d })}
                        max={form.to_date || undefined}
                      />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1.5">
                        <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 text-white flex items-center justify-center shadow-sm">
                          <CalendarDays size={12} />
                        </div>
                        <label className="text-sm font-semibold text-slate-800">To Date <span className="text-rose-500">*</span></label>
                      </div>
                      <DatePickerField
                        hideLabel
                        value={form.to_date}
                        onChange={(d) => setForm({ ...form, to_date: d })}
                        min={form.from_date || undefined}
                      />
                    </div>
                  </div>
                  {form.from_date && form.to_date && parseDateValue(form.to_date) < parseDateValue(form.from_date) && (
                    <p className="text-xs font-semibold text-rose-600 mt-1">To date cannot be before From date.</p>
                  )}
                  {rangeDayCount != null && (
                    <div className="text-sm font-bold text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-2">
                      Total Holidays: {rangeDayCount} Day{rangeDayCount !== 1 ? "s" : ""}
                    </div>
                  )}
                </>
              ) : null}

              <div>
                <div className="flex items-center gap-2 mb-1.5">
                  <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-fuchsia-500 to-pink-600 text-white flex items-center justify-center shadow-sm">
                    <PenSquare size={12} />
                  </div>
                  <label className="text-sm font-semibold text-slate-800">Holiday Name</label>
                </div>
                <input
                  ref={nameInputRef}
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Republic Day"
                  className="w-full border-2 border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:border-fuchsia-500 focus:ring-4 focus:ring-fuchsia-100 outline-none transition"
                />
              </div>

              <div>
                <div className="flex items-center gap-2 mb-1.5">
                  <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-violet-500 to-purple-600 text-white flex items-center justify-center shadow-sm">
                    <TypeIcon size={12} />
                  </div>
                  <label className="text-sm font-semibold text-slate-800">Holiday Type</label>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { value: "National", icon: ShieldCheck, gradient: "from-blue-500 to-indigo-600", soft: "from-blue-50 to-indigo-50", text: "text-blue-700", border: "border-blue-200", ring: "ring-blue-300" },
                    { value: "Optional", icon: Sparkles, gradient: "from-amber-500 to-orange-600", soft: "from-amber-50 to-orange-50", text: "text-amber-800", border: "border-amber-200", ring: "ring-amber-300" },
                  ].map((opt) => {
                    const Icon = opt.icon;
                    const active = form.holiday_type === opt.value;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setForm({ ...form, holiday_type: opt.value })}
                        className={`inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl border-2 text-sm font-bold transition-all duration-200 ${
                          active
                            ? `bg-gradient-to-br ${opt.gradient} text-white border-transparent shadow-md ring-4 ${opt.ring} -translate-y-0.5`
                            : `bg-gradient-to-br ${opt.soft} ${opt.text} ${opt.border} hover:-translate-y-0.5 hover:shadow`
                        }`}
                      >
                        <Icon size={14} /> {opt.value}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <div className="flex items-center gap-2 mb-1.5">
                  <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-teal-500 to-cyan-600 text-white flex items-center justify-center shadow-sm">
                    <FileText size={12} />
                  </div>
                  <label className="text-sm font-semibold text-slate-800">Description <span className="text-slate-400 text-xs font-normal">(optional)</span></label>
                </div>
                <textarea
                  rows={2}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="w-full border-2 border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:border-teal-500 focus:ring-4 focus:ring-teal-100 outline-none transition resize-none"
                />
              </div>

              <div className="pt-3 border-t border-slate-100 flex flex-wrap gap-2">
                {bulkEditIds.length > 0 ? (
                  <>
                    <button
                      type="submit"
                      disabled={bulkBusy}
                      className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold text-white bg-gradient-to-r from-indigo-500 to-blue-600 shadow-md hover:shadow-xl hover:-translate-y-0.5 transition disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                    >
                      <Pencil size={14} />
                      {bulkBusy
                        ? "Updating holidays…"
                        : `Update ${bulkEditIds.length} Holiday${bulkEditIds.length !== 1 ? "s" : ""}`}
                    </button>
                    <button
                      type="button"
                      onClick={resetForm}
                      disabled={bulkBusy}
                      className="inline-flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-semibold text-slate-700 hover:bg-slate-100 transition disabled:opacity-60"
                    >
                      Cancel
                    </button>
                  </>
                ) : !editId ? (
                  <button
                    type="submit"
                    disabled={addingHolidays || (holidayDuration === "range" && (!rangeDayCount || parseDateValue(form.to_date) < parseDateValue(form.from_date)))}
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold text-white bg-gradient-to-r from-emerald-500 to-green-600 shadow-md hover:shadow-xl hover:-translate-y-0.5 transition disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:shadow-md"
                  >
                    <Plus size={14} />
                    {addingHolidays
                      ? "Adding holidays…"
                      : holidayDuration === "range" && rangeDayCount
                        ? `Add ${rangeDayCount} Holiday${rangeDayCount !== 1 ? "s" : ""}`
                        : "Add Holiday"}
                  </button>
                ) : (
                  <button
                    type="submit"
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold text-white bg-gradient-to-r from-indigo-500 to-blue-600 shadow-md hover:shadow-xl hover:-translate-y-0.5 transition"
                  >
                    <Pencil size={14} /> Save Changes
                  </button>
                )}
                {editId && (
                  <button
                    type="button"
                    onClick={onDeleteSelected}
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold border-2 border-rose-300 text-rose-700 hover:bg-rose-50 hover:border-rose-400 transition"
                  >
                    <Trash2 size={14} /> Delete
                  </button>
                )}
                {editId && (
                  <button
                    type="button"
                    onClick={resetForm}
                    className="inline-flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-semibold text-slate-700 hover:bg-slate-100 transition"
                  >
                    Cancel
                  </button>
                )}
              </div>
            </form>
          </SectionCard>
          </div>
        </div>
      )}

      {/* 4. UPCOMING HOLIDAYS — full width horizontal */}
      {upcomingHolidays.length > 0 && (
        <SectionCard
          accent="from-[#1a437d] via-[#2a5fa3] to-sky-500"
          icon={CalendarClock}
          iconBg="from-[#1a437d] to-[#2a5fa3]"
          title="Upcoming Holidays"
          titleColor="text-[#0f2d52]"
          subtitle={`${upcomingHolidays.length} holiday(s) ahead in ${viewYear}.`}
        >
          <div className="flex gap-3 overflow-x-auto pb-1 -mx-1 px-1">
            {upcomingHolidays.slice(0, 12).map((h, idx) => {
              const days = daysUntil(h.holiday_date);
              const isNext = idx === 0;
              return (
                <div
                  key={h.holiday_id || `${h.holiday_date}-${idx}`}
                  className={`relative shrink-0 w-44 rounded-2xl border-2 p-3 transition hover:-translate-y-0.5 hover:shadow-lg ${
                    isNext
                      ? "bg-gradient-to-br from-[#0f2d52] via-[#1a437d] to-[#2a5fa3] border-transparent text-white shadow-lg shadow-[#1a437d]/30"
                      : h.is_optional
                        ? "bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200"
                        : "bg-gradient-to-br from-sky-50 to-indigo-50 border-sky-200"
                  }`}
                >
                  {isNext && (
                    <span className="absolute -top-2 left-3 px-2 py-0.5 rounded-full bg-white text-[#1a437d] text-[9px] font-extrabold uppercase tracking-wider shadow">
                      Next up
                    </span>
                  )}
                  <div className={`text-[10px] uppercase tracking-wider font-bold ${isNext ? "text-white/90" : "text-slate-500"}`}>
                    {dayOfWeek(h.holiday_date)}
                  </div>
                  <div className={`text-2xl font-extrabold mt-0.5 ${isNext ? "text-white drop-shadow" : "text-slate-900"}`}>
                    {formatHolDate(h.holiday_date).split(",")[0]}
                  </div>
                  <div className={`text-sm font-semibold mt-1 line-clamp-2 ${isNext ? "text-white" : "text-slate-800"}`}>
                    {h.name || "Holiday"}
                  </div>
                  <div className="mt-2 flex items-center justify-between gap-2">
                    {h.is_optional ? (
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${isNext ? "bg-white/25 text-white" : "bg-amber-100 text-amber-800 border border-amber-200"}`}>
                        <Sparkles size={9} /> Optional
                      </span>
                    ) : (
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${isNext ? "bg-white/25 text-white" : "bg-blue-100 text-blue-800 border border-blue-200"}`}>
                        <ShieldCheck size={9} /> National
                      </span>
                    )}
                    <span className={`text-[10px] font-extrabold ${isNext ? "text-white" : "text-slate-600"}`}>
                      {days === 0 ? "Today" : days === 1 ? "Tomorrow" : `in ${days}d`}
                    </span>
                  </div>
                </div>
              );
            })}
            <div className="shrink-0 flex items-center">
              <button
                type="button"
                onClick={() => {
                  setShowAllHolidays(true);
                  const el = document.getElementById("holiday-list-section");
                  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                  else window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
                }}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold text-[#1a437d] hover:bg-sky-50 transition"
              >
                View all <ArrowRight size={12} />
              </button>
            </div>
          </div>
        </SectionCard>
      )}

      {/* 5. HOLIDAY LIST — full width horizontal */}
      <div id="holiday-list-section">
        <SectionCard
          accent="from-[#0f2d52] via-[#1a437d] to-[#2a5fa3]"
          icon={CalendarDays}
          iconBg="from-[#0f2d52] to-[#1a437d]"
          title={showAllHolidays ? `Holiday List — All ${viewYear}` : `Holiday List — ${MONTHS[selectedMonth - 1]} ${viewYear}`}
          titleColor="text-[#0f2d52]"
        >
          {canManage && (
            <div className="mb-3 flex items-start gap-2.5 rounded-xl border-2 border-[#1a437d]/25 bg-gradient-to-r from-sky-50 via-indigo-50 to-blue-50 px-3.5 py-3 shadow-sm">
              <div className="shrink-0 w-8 h-8 rounded-lg bg-gradient-to-br from-[#0f2d52] to-[#1a437d] text-white flex items-center justify-center shadow-sm">
                <ListChecks size={15} />
              </div>
              <p className="text-xs sm:text-sm font-semibold text-[#0f2d52] leading-relaxed pt-0.5">
                Use <span className="font-extrabold text-[#1a437d]">checkboxes</span> for bulk actions, or{" "}
                <span className="inline-flex items-center gap-0.5 font-extrabold text-indigo-700">
                  <Pencil size={12} /> Edit
                </span>{" "}
                /{" "}
                <span className="inline-flex items-center gap-0.5 font-extrabold text-rose-700">
                  <Trash2 size={12} /> Delete
                </span>{" "}
                per row.
              </p>
            </div>
          )}
          {canManage && selectedIds.size > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3 px-3 py-2.5 rounded-xl bg-gradient-to-r from-[#0f2d52] via-[#1a437d] to-[#2a5fa3] text-white shadow-md">
              <span className="text-sm font-bold">
                {selectedIds.size} holiday{selectedIds.size !== 1 ? "s" : ""} selected
              </span>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={startBulkEdit}
                  disabled={bulkBusy}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold bg-white/20 hover:bg-white/30 text-white transition disabled:opacity-60"
                >
                  <Pencil size={13} /> Edit Selected
                </button>
                <button
                  type="button"
                  onClick={onBulkDelete}
                  disabled={bulkBusy}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold bg-white/20 hover:bg-white/30 text-white transition disabled:opacity-60"
                >
                  <Trash2 size={13} /> Delete Selected
                </button>
                <button
                  type="button"
                  onClick={clearSelection}
                  disabled={bulkBusy}
                  className="text-xs font-semibold text-white/80 hover:text-white transition disabled:opacity-60"
                >
                  Clear
                </button>
              </div>
            </div>
          )}
          <div className="overflow-x-auto -mx-1 px-1">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gradient-to-r from-sky-50 via-indigo-50 to-slate-50 border-y border-indigo-100">
                  {canManage && (
                    <th className="w-10 px-2 py-2.5">
                      <input
                        type="checkbox"
                        className="rounded border-indigo-300 text-indigo-600 focus:ring-indigo-500"
                        checked={allDisplayedSelected}
                        onChange={(e) => toggleSelectAllDisplayed(e.target.checked)}
                        aria-label="Select all displayed holidays"
                        disabled={displayHolidays.length === 0 || bulkBusy}
                      />
                    </th>
                  )}
                  <th className="text-left px-3 py-2.5 text-[11px] uppercase tracking-wider font-bold text-[#0f2d52]">Date</th>
                  <th className="text-left px-3 py-2.5 text-[11px] uppercase tracking-wider font-bold text-[#0f2d52]">Holiday Name</th>
                  <th className="text-left px-3 py-2.5 text-[11px] uppercase tracking-wider font-bold text-[#0f2d52]">Type</th>
                  <th className="text-left px-3 py-2.5 text-[11px] uppercase tracking-wider font-bold text-[#0f2d52]">Description</th>
                  {canManage && (
                    <th className="text-right px-3 py-2.5 text-[11px] uppercase tracking-wider font-bold text-[#0f2d52]">Actions</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {displayHolidays.map((h) => {
                  const isEditing = editId === h.holiday_id;
                  const isChecked = selectedIds.has(h.holiday_id);
                  return (
                    <tr
                      key={h.holiday_id}
                      className={`border-b border-slate-100 transition ${
                        isEditing
                          ? "bg-gradient-to-r from-sky-50 to-indigo-50 ring-2 ring-indigo-300 ring-inset"
                          : isChecked
                            ? "bg-indigo-50/70"
                            : "hover:bg-slate-50/70"
                      }`}
                    >
                      {canManage && (
                        <td className="px-2 py-2.5">
                          <input
                            type="checkbox"
                            className="rounded border-indigo-300 text-indigo-600 focus:ring-indigo-500"
                            checked={isChecked}
                            onChange={() => toggleHolidaySelection(h.holiday_id)}
                            aria-label={`Select ${h.name || "holiday"} on ${formatHolDate(h.holiday_date)}`}
                            disabled={bulkBusy}
                          />
                        </td>
                      )}
                      <td className="px-3 py-2.5 font-semibold text-slate-800">{formatHolDate(h.holiday_date)}</td>
                      <td className="px-3 py-2.5 text-slate-700">{h.name || "—"}</td>
                      <td className="px-3 py-2.5">
                        {h.is_optional ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[11px] font-bold border border-amber-200">
                            <Sparkles size={10} /> Optional
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 text-[11px] font-bold border border-blue-200">
                            <ShieldCheck size={10} /> National
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-slate-500">—</td>
                      {canManage && (
                        <td className="px-3 py-2.5">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              type="button"
                              onClick={() => startEdit(h)}
                              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-bold text-indigo-700 bg-indigo-50 border border-indigo-200 hover:bg-indigo-100 hover:border-indigo-300 transition"
                              title="Edit holiday"
                            >
                              <Pencil size={13} /> Edit
                            </button>
                            <button
                              type="button"
                              onClick={() => onDelete(h)}
                              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-bold text-rose-700 bg-rose-50 border border-rose-200 hover:bg-rose-100 hover:border-rose-300 transition"
                              title="Delete holiday"
                            >
                              <Trash2 size={13} /> Delete
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
                {!displayHolidays.length && !loading && (
                  <tr>
                    <td colSpan={canManage ? 6 : 4} className="text-center py-10 text-slate-400">
                      <CalendarDays size={28} className="mx-auto mb-2 text-slate-300" />
                      <p className="text-sm font-medium">{showAllHolidays ? `No holidays for ${viewYear}.` : "No holidays for this month."}</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="pt-3 mt-2 border-t border-slate-100">
            <button
              type="button"
              onClick={() => setShowAllHolidays((v) => !v)}
              className="inline-flex items-center gap-1.5 text-xs font-bold text-[#1a437d] hover:text-[#0f2d52] underline-offset-4 hover:underline"
            >
              {showAllHolidays ? `View ${MONTHS[selectedMonth - 1]} only` : `View All Holidays (${viewYear})`}
            </button>
          </div>
        </SectionCard>
      </div>
    </div>
  );
}
